import { useEffect, useRef, useState } from 'react';
import { initGame } from '../../scripts/main';
import { useTonConnectUI, useTonWallet } from '@tonconnect/ui-react';
import MobileControls from './MobileControls';
import '../styles/Game.css';
import { useUser } from '@/context/UserContext';
import { GameSidebar } from './GameSidebar';
import { useToast } from '@/context/ToastContext';
import { isLandscapeViewport, requestFullscreenLandscape, requestNativeOrientation } from '@/utils/orientation';
import { isMobileDevice } from '../../scripts/device';
import { blockToColour } from '../../scripts/blocks';

const Game = () => {
    const mountRef = useRef<HTMLDivElement>(null);
    const resizeRef = useRef<(() => void) | null>(null);
    const [isMobile, setIsMobile] = useState(false);
    const [gameInitializing, setGameInitializing] = useState(true);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isLandscape, setIsLandscape] = useState(() => isLandscapeViewport());
    const [useFallbackLandscape, setUseFallbackLandscape] = useState(false);

    const [player, setPlayer] = useState<any>(null);

    const wallet = useTonWallet();
    const [tonConnectUI] = useTonConnectUI();
    const { user } = useUser();
    const { showToast } = useToast();

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(isMobileDevice());
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (!isMobile) return;
        requestFullscreenLandscape();
        setIsLandscape(true);
    }, [isMobile]);

    useEffect(() => {
        const syncOrientationState = () => {
            if (!useFallbackLandscape) {
                setIsLandscape(isLandscapeViewport());
            }
        };

        syncOrientationState();
        window.addEventListener('resize', syncOrientationState);
        window.addEventListener('orientationchange', syncOrientationState);
        window.screen?.orientation?.addEventListener?.('change', syncOrientationState);

        return () => {
            window.removeEventListener('resize', syncOrientationState);
            window.removeEventListener('orientationchange', syncOrientationState);
            window.screen?.orientation?.removeEventListener?.('change', syncOrientationState);
        };
    }, [useFallbackLandscape]);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            if (resizeRef.current) {
                resizeRef.current();
            }
        }, 60);

        return () => window.clearTimeout(timer);
    }, [useFallbackLandscape, isLandscape]);

    useEffect(() => {
        if (!mountRef.current) {
            console.error("Game Mount Ref is null!");
            return;
        }

        console.log("Initializing Game... User:", user?.uid);

        // Initialize the game
        try {
            const { cleanup, player: newPlayer, world, resize } = initGame(mountRef.current, user?.uid || 'guest');
            console.log("Game initialized successfully. Player created.");

            // Notification Bridge
            if (world) {
                world.on('notify', (data: { text: string, type: string }) => {
                    showToast(data.text, data.type as any);
                });
            }

            setPlayer(newPlayer);
            resizeRef.current = resize;
            setGameInitializing(false);

            return () => {
                console.log("Cleaning up Game...");
                if (cleanup && typeof cleanup === 'function') {
                    cleanup();
                    console.log("Game cleanup done.");
                }
            };
        } catch (error) {
            console.error("CRITICAL ERROR initializing game:", error);
            // Consider setting a state to show an error message on screen
            setGameInitializing(false);
            return () => { };
        }

    }, [user?.uid]);

    // Update controls enabled state when menu opens/closes
    useEffect(() => {
        if (player && typeof player.setControlsEnabled === 'function') {
            player.setControlsEnabled(!isMenuOpen);
        }
        if (!isMenuOpen && resizeRef.current) {
            resizeRef.current();
        }
    }, [isMenuOpen, player]);

    const handleConnectClick = () => {
        if (wallet) {
            tonConnectUI.disconnect();
        } else {
            tonConnectUI.openModal();
        }
    };

    const toggleOrientation = async () => {
        const targetLandscape = !(useFallbackLandscape || isLandscape);
        const nativeApplied = await requestNativeOrientation(targetLandscape);
        setUseFallbackLandscape(nativeApplied ? false : targetLandscape);
        setIsLandscape(targetLandscape);
    };

    return (
        <div className={`game-container ${useFallbackLandscape ? 'force-landscape' : ''} ${isMenuOpen ? 'menu-open' : ''}`}>
            <div ref={mountRef} className="three-container" />

            {isMobile && player && !isMenuOpen && (
                <MobileControls player={player} onOpenMenu={() => setIsMenuOpen(true)} />
            )}

            {(isMenuOpen || !isMobile) && (
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: isMenuOpen ? 'auto' : 'none', zIndex: 2000 }}>
                    {isMenuOpen && <GameSidebar onClose={() => setIsMenuOpen(false)} player={player} />}
                </div>
            )}

            <div id="toolbar-container">
                <div id="toolbar">
                    {Object.entries(blockToColour).map(([name, color], index) => (
                        <div
                            key={index + 1}
                            id={`toolbar-${index + 1}`}
                            className="toolbar-icon"
                            title={name}
                            style={{
                                backgroundColor: color,
                                width: '32px',
                                height: '32px',
                                margin: '4px',
                                borderRadius: '4px',
                                boxSizing: 'border-box'
                            }}
                        />
                    ))}
                    <img
                        className="toolbar-icon selected"
                        id="toolbar-0"
                        src="/textures/pickaxe.png"
                        alt="pickaxe"
                    />
                </div>
            </div>

            <div className="ui-overlay">
                <div className="game-top-controls">
                    {!isMobile && (
                        <button
                            className="game-control-btn"
                            onClick={() => setIsMenuOpen(true)}
                            title="Menu"
                        >
                            ☰
                        </button>
                    )}
                    <button
                        className="game-control-btn"
                        onClick={toggleOrientation}
                        title={isLandscape ? "Vertical Mode" : "Landscape Mode"}
                    >
                        {isLandscape ? "📱" : "🔄"}
                    </button>
                </div>
                {/* Add UI elements here if needed, e.g. crosshair for desktop */}
                {!isMobile && <div className="crosshair">+</div>}
            </div>
        </div>
    );
};

export default Game;
